// components/logo.tsx
import { useBrandConfig } from "@/components/layout/brand-provider";
import { cn } from "@/lib/shared/utils";
import React from "react";

interface LogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  variant?: "full" | "icon-only" | "custom";
  /**
   * Scale factor for custom logos to handle SVGs with excessive whitespace
   * 1.0 = normal size, 1.5 = 150% size, 2.0 = 200% size, etc.
   */
  scale?: number;
  /**
   * Color for the logo icon. Defaults to the theme's primary color (var(--primary)).
   * Use "currentColor" to inherit from parent text color, or any valid CSS color.
   */
  iconColor?: string;
}

export function Logo({ className, size = "md", variant = "full", scale = 1.0, iconColor = "var(--primary)" }: LogoProps) {
  const { brandConfig } = useBrandConfig();
  
  const sizeClasses = {
    // For Condota "full" logo, icon size should match text cap height
    // For custom logos, we use generous width-based sizing for better visibility
    sm: variant === "custom" ? "w-32 h-auto max-h-10" : variant === "full" ? "h-4" : "h-6 w-6",
    md: variant === "custom" ? "w-40 h-auto max-h-16" : variant === "full" ? "h-5" : "h-8 w-8",
    lg: variant === "custom" ? "w-52 h-auto max-h-24" : variant === "full" ? "h-7" : "h-12 w-12", // sidebar
    xl: variant === "custom" ? "w-64 h-auto max-h-32" : variant === "full" ? "h-10" : "h-16 w-16",
    hero: variant === "custom" ? "w-96 h-auto max-h-48" : variant === "full" ? "h-14" : "h-24 w-24",
  };

  // If using custom variant, render ONLY the brand-configured logo (no text)
  if (variant === "custom") {
    const scaledStyle = scale !== 1.0 ? {
      transform: `scale(${scale})`,
      transformOrigin: 'center center'
    } : {};
    
    return (
      <div className={cn(sizeClasses[size], "flex-shrink-0 overflow-hidden", className)}>
        <img
          src={brandConfig.logoUrl}
          alt={`${brandConfig.shortName} Logo`}
          className="w-full h-full object-contain"
          style={scaledStyle}
          onError={(e) => {
            // Fallback to Condota logo if custom logo fails to load
            console.warn(`Failed to load custom logo: ${brandConfig.logoUrl}, falling back to default`);
            (e.target as HTMLImageElement).src = "/logo.svg";
          }}
        />
      </div>
    );
  }

  if (variant === "icon-only") {
    return (
      <svg
        className={cn(sizeClasses[size], "flex-shrink-0", className)}
        viewBox="0 0 1228 1228"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Condota Logo"
      >
        <path
          d="m 212.43621,0.83424294 c 23.756,-1.468 54.968,-0.563 79.092,-0.51 46.515,0.285 93.031,0.318 139.546,0.101 l 408.439,-0.231 122.13,-0.115 c 28.06,0.111 65.96999,-0.822 92.85999,4.94499996 41.06,8.9140001 78.62,29.6600001 108.02,59.6760001 27.84,27.97 49.98,65.644997 58.8,104.186997 6.93,30.256 6.34,64.826 6.34,95.682 l -0.01,83.138 -0.28,291.597 c -118.61,1.57 -239.51999,-1.91 -357.78999,0.45 l -0.17,120.28 c 0.02,26.71 5.03,68.17 -11.58,89.81 -18.36,23.94 -52.71,19.74 -79.65,19.58 l -87.06,-0.02 c -17.2421,-0.154 14.516,-0.387 -2.7257,-0.2383 -2.59,119.4054 -2.7963,239.24186 -1.7,358.67266 -16.91,0.29 -80.8043,-0.4645 -97.8703,0.1555 l 0.58,-149.64 c -0.304,-69.85 -1.445,-138.53006 -0.66,-208.60006 -42.547,-2.07 -86.665,0.72 -129.392,-0.28 -27.451,-0.64 -62.245,6.3 -84.022,-14.64 -8.28,-7.8 -13.834,-18.05 -15.844,-29.25 -1.586,-8.74 -1.171,-28.93 -1.186,-38.75 l -0.015,-66.7 -0.098,-246.964 c -0.068,-25.445 -1.281,-53.139 2.699,-78.06 2.417,-15.13 22.062,-33.733 37.449,-35.463 17.766,-1.998 37.258,-1.341 55.214,-1.306 l 89.03,0.087 169.351,0.007 c 20.27,-0.033 12.7535,-0.18779 59.77,0.006 27.4964,0.41623 60.1087,-5.30713 83.22,16.661 6.66,7.755 11.35,15.213 13.02,25.799 2.21,21.875 1.69,45.921 1.54,67.996 -0.28,39.787 0.64,46.60776 -0.22,70.0315 99.22,-0.5054 156.20179,-0.48712 238.25299,0.056 1.2653,-23.14893 -0.05,-47.8064 -0.058,-71.15053 l -0.099,-146.573 c -0.01,-44.941 2.2824,-115.62748 -6.3511,-142.957 -4.8294,-15.27931 -17.3248,-29.95021 -31.0607,-41.26857 -29.4812,-24.28945 -60.9553,-32.96293 -103.94079,-32.97566 -47.2361,-0.0134 -94.9436,0.003 -142.3533,-0.003 l -351.847,0.009 -169.327,-0.0191 c -35.205,0.001 -73.535,-43.949407 -108.124,-39.079407 -46.677,6.572 -90.086,39.867997 -112.608006,80.608997 -16.071,29.07 -19.998,62.121 -20.477,94.854 -0.003,52.35 0.151,104.739 0.11,157.112 l -0.097,362.00207 -0.481,162.33 c -0.068,73.34 -6.811,139.90006 49.704006,196.35006 36.614,36.58 76.838,47.03 127.432,48.97 37.737,1.44 74.685,0.63 112.178,0.47 l 140.09716,-1.0771 0.12511,60.68 -146.66427,0.9171 -73.514,-0.02 c -78.945,-0.06 -135.516,-2.81 -197.603006,-59.86 -43.49,-39.17 -69.3349997,-94.24 -71.68199976,-152.72 -0.979,-22.63006 -0.546,-47.29006 -0.539,-70.10006 l 0.027,-110.22 -0.049,-336.89807 -0.1,-194.091 c -0.012,-32.46 -1.50799994,-90.604 3.57700006,-120.793 6.8819997,-41.445 25.7539997,-79.967 54.2849997,-110.806997 41.094,-44.545 93.965006,-69.3700001 154.360006,-71.84100006 z M 510.97221,716.92424 c 68.443,0.51 136.891,0.52 205.331,0.01 0.86,-16.33 0.08,-37.1 -0.03,-53.69 l -0.19,-97.759 c -0.02,-16.669 -0.63,-37.936 0.02,-54.152 l -133.266,0.215 c -22.052,-0.035 -49.342,-0.812 -70.97,0.133 -1.517,17.439 -0.984,39.696 -0.981,57.46 l 0.103,94.833 c 0.057,17.21 0.73,35.93 -0.017,52.95 z"
          fill={iconColor}
        />
        <path
          d="m 1227.9403,736.47174 -0.3571,213.7725 c 0.29,87.65006 -0.09,144.15006 -67.43,214.12006 -24.68,25.64 -68.69,49.46 -103.19,57.03 -40.48,8.88 -96.13999,6.33 -138.65999,6.34 h -187.86 l -92.0924,0.066 1.7993,-99.4858 202.2742,0.9212 c 32.36,0 90.1587,0.1035 122.3787,-0.2465 33.59,-0.36 63.45309,4.3282 89.49309,-17.0418 60,-49.24 52.8324,-77.5101 52.7524,-148.82016 l 0.3477,-65.9832 1.3342,-157.9558 -0.8311,-3.4582 z"
          fill={iconColor}
        />
      </svg>
    );
  }

  return (
    <div className={cn("flex items-center gap-4", className)}>
      {/* Condota logo icon */}
      <svg
        className={cn(sizeClasses[size], "flex-shrink-0")}
        viewBox="0 0 1228 1228"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Condota Logo"
      >
        <path
          d="m 212.43621,0.83424294 c 23.756,-1.468 54.968,-0.563 79.092,-0.51 46.515,0.285 93.031,0.318 139.546,0.101 l 408.439,-0.231 122.13,-0.115 c 28.06,0.111 65.96999,-0.822 92.85999,4.94499996 41.06,8.9140001 78.62,29.6600001 108.02,59.6760001 27.84,27.97 49.98,65.644997 58.8,104.186997 6.93,30.256 6.34,64.826 6.34,95.682 l -0.01,83.138 -0.28,291.597 c -118.61,1.57 -239.51999,-1.91 -357.78999,0.45 l -0.17,120.28 c 0.02,26.71 5.03,68.17 -11.58,89.81 -18.36,23.94 -52.71,19.74 -79.65,19.58 l -87.06,-0.02 c -17.2421,-0.154 14.516,-0.387 -2.7257,-0.2383 -2.59,119.4054 -2.7963,239.24186 -1.7,358.67266 -16.91,0.29 -80.8043,-0.4645 -97.8703,0.1555 l 0.58,-149.64 c -0.304,-69.85 -1.445,-138.53006 -0.66,-208.60006 -42.547,-2.07 -86.665,0.72 -129.392,-0.28 -27.451,-0.64 -62.245,6.3 -84.022,-14.64 -8.28,-7.8 -13.834,-18.05 -15.844,-29.25 -1.586,-8.74 -1.171,-28.93 -1.186,-38.75 l -0.015,-66.7 -0.098,-246.964 c -0.068,-25.445 -1.281,-53.139 2.699,-78.06 2.417,-15.13 22.062,-33.733 37.449,-35.463 17.766,-1.998 37.258,-1.341 55.214,-1.306 l 89.03,0.087 169.351,0.007 c 20.27,-0.033 12.7535,-0.18779 59.77,0.006 27.4964,0.41623 60.1087,-5.30713 83.22,16.661 6.66,7.755 11.35,15.213 13.02,25.799 2.21,21.875 1.69,45.921 1.54,67.996 -0.28,39.787 0.64,46.60776 -0.22,70.0315 99.22,-0.5054 156.20179,-0.48712 238.25299,0.056 1.2653,-23.14893 -0.05,-47.8064 -0.058,-71.15053 l -0.099,-146.573 c -0.01,-44.941 2.2824,-115.62748 -6.3511,-142.957 -4.8294,-15.27931 -17.3248,-29.95021 -31.0607,-41.26857 -29.4812,-24.28945 -60.9553,-32.96293 -103.94079,-32.97566 -47.2361,-0.0134 -94.9436,0.003 -142.3533,-0.003 l -351.847,0.009 -169.327,-0.0191 c -35.205,0.001 -73.535,-43.949407 -108.124,-39.079407 -46.677,6.572 -90.086,39.867997 -112.608006,80.608997 -16.071,29.07 -19.998,62.121 -20.477,94.854 -0.003,52.35 0.151,104.739 0.11,157.112 l -0.097,362.00207 -0.481,162.33 c -0.068,73.34 -6.811,139.90006 49.704006,196.35006 36.614,36.58 76.838,47.03 127.432,48.97 37.737,1.44 74.685,0.63 112.178,0.47 l 140.09716,-1.0771 0.12511,60.68 -146.66427,0.9171 -73.514,-0.02 c -78.945,-0.06 -135.516,-2.81 -197.603006,-59.86 -43.49,-39.17 -69.3349997,-94.24 -71.68199976,-152.72 -0.979,-22.63006 -0.546,-47.29006 -0.539,-70.10006 l 0.027,-110.22 -0.049,-336.89807 -0.1,-194.091 c -0.012,-32.46 -1.50799994,-90.604 3.57700006,-120.793 6.8819997,-41.445 25.7539997,-79.967 54.2849997,-110.806997 41.094,-44.545 93.965006,-69.3700001 154.360006,-71.84100006 z M 510.97221,716.92424 c 68.443,0.51 136.891,0.52 205.331,0.01 0.86,-16.33 0.08,-37.1 -0.03,-53.69 l -0.19,-97.759 c -0.02,-16.669 -0.63,-37.936 0.02,-54.152 l -133.266,0.215 c -22.052,-0.035 -49.342,-0.812 -70.97,0.133 -1.517,17.439 -0.984,39.696 -0.981,57.46 l 0.103,94.833 c 0.057,17.21 0.73,35.93 -0.017,52.95 z"
          fill={iconColor}
        />
        <path
          d="m 1227.9403,736.47174 -0.3571,213.7725 c 0.29,87.65006 -0.09,144.15006 -67.43,214.12006 -24.68,25.64 -68.69,49.46 -103.19,57.03 -40.48,8.88 -96.13999,6.33 -138.65999,6.34 h -187.86 l -92.0924,0.066 1.7993,-99.4858 202.2742,0.9212 c 32.36,0 90.1587,0.1035 122.3787,-0.2465 33.59,-0.36 63.45309,4.3282 89.49309,-17.0418 60,-49.24 52.8324,-77.5101 52.7524,-148.82016 l 0.3477,-65.9832 1.3342,-157.9558 -0.8311,-3.4582 z"
          fill={iconColor}
        />
      </svg>

      <span
        className={cn(
          "font-bold text-foreground dark:text-white tracking-tight",
          {
            "text-sm": size === "sm",
            "text-lg": size === "md",
            "text-3xl": size === "lg",
            "text-5xl": size === "xl",
            "text-7xl": size === "hero",
          }
        )}
      >
        condota
      </span>
    </div>
  );
}

/**
 * Smart Logo component that automatically chooses the appropriate variant
 * based on brand configuration
 */
export function SmartLogo({ className, size = "md" }: Omit<LogoProps, 'variant' | 'scale'>) {
  const { brandConfig } = useBrandConfig();
  // Use custom variant if we have custom branding, otherwise use the Condota full logo
  const variant = brandConfig.logoUrl !== "/logo.svg" ? "custom" : "full";
  // Default scale of 1.8 for custom logos to compensate for common SVG whitespace issues
  const scale = variant === "custom" ? 1.0 : 1.0;
  return <Logo className={className} size={size} variant={variant} scale={scale} />;
}

/**
 * Smart Icon Logo component that automatically chooses the appropriate variant
 * for icon-only display based on brand configuration
 */
export function SmartIconLogo({ className, size = "md" }: Omit<LogoProps, 'variant' | 'scale'>) {
  const { brandConfig } = useBrandConfig();
  // Use custom variant if we have custom branding, otherwise use the Condota icon
  if (brandConfig.logoUrl !== "/logo.svg") {
    // Default scale of 1.6 for custom logo icons to compensate for SVG whitespace
    const scaledStyle = {
      transform: 'scale(1.6)',
      transformOrigin: 'center center'
    };
    
    return (
      <div className={cn(
        {
          // For icon usage, we use fixed square dimensions that work well for icons
          "w-8 h-8": size === "sm",
          "w-10 h-10": size === "md", 
          "w-12 h-12": size === "lg",
          "w-16 h-16": size === "xl",
        },
        "flex-shrink-0 overflow-hidden",
        className
      )}>
        <img
          src={brandConfig.logoUrl}
          alt={`${brandConfig.shortName} Logo`}
          className="w-full h-full object-contain"
          style={scaledStyle}
          onError={(e) => {
            console.warn(`Failed to load custom logo: ${brandConfig.logoUrl}, falling back to default`);
            (e.target as HTMLImageElement).src = "/logo.svg";
          }}
        />
      </div>
    );
  }
  
  return <Logo className={className} size={size} variant="icon-only" />;
}

// Usage examples component for reference
export function LogoExamples() {
  return (
    <div className="p-6 space-y-6 bg-background">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Logo Variants</h3>

        <div className="flex flex-wrap items-center gap-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Small</p>
            <Logo size="sm" />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Medium</p>
            <Logo size="md" />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Large</p>
            <Logo size="lg" />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Extra Large</p>
            <Logo size="xl" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Icon Only Variants</h3>

        <div className="flex flex-wrap items-center gap-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Original Icon</p>
            <Logo variant="icon-only" size="lg" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Usage in Different Contexts</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Navbar example */}
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Navbar</p>
            <div className="flex items-center justify-between bg-card p-3 rounded border">
              <Logo size="sm" />
              <div className="text-sm text-muted-foreground">Menu items...</div>
            </div>
          </div>

          {/* Sidebar example */}
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Sidebar</p>
            <div className="bg-sidebar p-3 rounded border">
              <Logo size="md" className="mb-4" />
              <div className="text-sm text-sidebar-foreground">
                Navigation...
              </div>
            </div>
          </div>

          {/* Login page example */}
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Login Page</p>
            <div className="bg-card p-6 rounded border text-center">
              <Logo size="xl" className="justify-center mb-4" />
              <div className="text-sm text-muted-foreground">Login form...</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
